// src/manager.ts
import { Store } from "@tanstack/store";
import algosdk8 from "algosdk";

// src/network.ts
var NetworkId = /* @__PURE__ */ ((NetworkId2) => {
  NetworkId2["MAINNET"] = "mainnet";
  NetworkId2["TESTNET"] = "testnet";
  NetworkId2["BETANET"] = "betanet";
  NetworkId2["LOCALNET"] = "localnet";
  return NetworkId2;
})(NetworkId || {});
function isValidNetworkId(networkId) {
  return Object.values(NetworkId).includes(networkId);
}
function isNetworkConfigMap(config) {
  const networkKeys = Object.values(NetworkId);
  return Object.keys(config).some((key) => networkKeys.includes(key));
}
var nodeServerMap = {
  ["mainnet" /* MAINNET */]: "https://mainnet-api.algonode.cloud",
  ["testnet" /* TESTNET */]: "https://testnet-api.algonode.cloud",
  ["betanet" /* BETANET */]: "https://betanet-api.algonode.cloud",
  ["localnet" /* LOCALNET */]: "http://localhost"
};
function createDefaultNetworkConfig() {
  return Object.values(NetworkId).reduce((acc, value) => {
    acc[value] = {
      token: "",
      baseServer: nodeServerMap[value],
      port: "",
      headers: {}
    };
    return acc;
  }, {});
}
var caipChainId = {
  ["mainnet" /* MAINNET */]: "algorand:wGHE2Pwdvd7S12BL5FaOP20EGYesN73k",
  ["testnet" /* TESTNET */]: "algorand:SGO1GKSzyE7IEPItTxCByw9x8FmnrCDe",
  ["betanet" /* BETANET */]: "algorand:mFgazF-2uRS1tMiL9dsj01hJGySEmPN2"
};

// src/wallets/base.ts
var BaseWallet = class {
  id;
  metadata;
  store;
  subscribe;
  constructor({ id, metadata, store, subscribe }) {
    this.id = id;
    this.store = store;
    this.subscribe = subscribe;
    const ctor = this.constructor;
    this.metadata = { ...ctor.defaultMetadata, ...metadata };
  }
  static defaultMetadata = { name: "Base Wallet", icon: "" };
  setActive() {
    console.info(`[Wallet] Set active wallet: ${this.id}`);
    setActiveWallet(this.store, { walletId: this.id });
  }
  setActiveAccount(account) {
    console.info(`[Wallet] Set active account: ${account}`);
    setActiveAccount(this.store, {
      walletId: this.id,
      address: account
    });
  }
  // ---------- Derived Properties ------------------------------------ //
  get accounts() {
    const state = this.store.state;
    const walletState = state.wallets[this.id];
    return walletState ? walletState.accounts : [];
  }
  get addresses() {
    return this.accounts.map((account) => account.address);
  }
  get activeAccount() {
    const state = this.store.state;
    const walletState = state.wallets[this.id];
    return walletState ? walletState.activeAccount : null;
  }
  get activeAddress() {
    return this.activeAccount?.address ?? null;
  }
  get activeNetwork() {
    const state = this.store.state;
    return state.activeNetwork;
  }
  get isConnected() {
    const state = this.store.state;
    const walletState = state.wallets[this.id];
    return walletState ? walletState.accounts.length > 0 : false;
  }
  get isActive() {
    const state = this.store.state;
    return state.activeWallet === this.id;
  }
  // ---------- Protected Methods ------------------------------------- //
  onDisconnect() {
    removeWallet(this.store, { walletId: this.id });
  }
};

// src/wallets/defly.ts
import algosdk7 from "algosdk";

// src/utils.ts
import algosdk6 from "algosdk";

// src/wallets/types.ts
var WalletId = /* @__PURE__ */ ((WalletId2) => {
  WalletId2["DEFLY"] = "defly";
  WalletId2["EXODUS"] = "exodus";
  WalletId2["KMD"] = "kmd";
  WalletId2["MNEMONIC"] = "mnemonic";
  WalletId2["PERA"] = "pera";
  WalletId2["WALLETCONNECT"] = "walletconnect";
  return WalletId2;
})(WalletId || {});

// src/wallets/exodus.ts
import algosdk from "algosdk";
var icon = "data:image/svg+xml;base64,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";
var ExodusWallet = class extends BaseWallet {
  client = null;
  options;
  store;
  constructor({
    id,
    store,
    subscribe,
    options = {},
    metadata = {}
  }) {
    super({ id, metadata, store, subscribe });
    this.options = options;
    this.store = store;
  }
  static defaultMetadata = { name: "Exodus", icon };
  async initializeClient() {
    console.info("[ExodusWallet] Initializing client...");
    if (typeof window === "undefined" || window.algorand === void 0) {
      throw new Error("Exodus is not available.");
    }
    const client = window.algorand;
    this.client = client;
    return client;
  }
  async connect() {
    console.info("[ExodusWallet] Connecting...");
    try {
      const client = this.client || await this.initializeClient();
      const { accounts } = await client.enable(this.options);
      if (accounts.length === 0) {
        throw new Error("No accounts found!");
      }
      const walletAccounts = accounts.map((address, idx) => ({
        name: `Exodus Wallet ${idx + 1}`,
        address
      }));
      const activeAccount = walletAccounts[0];
      addWallet(this.store, {
        walletId: this.id,
        wallet: {
          accounts: walletAccounts,
          activeAccount
        }
      });
      return walletAccounts;
    } catch (error) {
      if (error.name === "UserRejectedRequestError") {
        console.info("[ExodusWallet] Connection cancelled.");
      } else {
        console.error(`[ExodusWallet] Error connecting: ${error.message}`);
      }
      return [];
    }
  }
  async disconnect() {
    console.info("[ExodusWallet] Disconnecting...");
    this.onDisconnect();
  }
  async resumeSession() {
    const state = this.store.state;
    const walletState = state.wallets[this.id];
    if (!walletState) {
      return;
    }
    console.info("[ExodusWallet] Resuming session...");
    if (window === void 0 || window.algorand === void 0 || window.algorand.isConnected !== true) {
      this.onDisconnect();
    }
  }
  async signTransactions(txnGroup, indexesToSign, returnGroup = true) {
    if (!this.client) {
      throw new Error("[ExodusWallet] Client not initialized!");
    }
    const txnsToSign = [];
    const signedIndexes = [];
    const msgpackTxnGroup = normalizeTxnGroup(txnGroup);
    const decodedObjects = msgpackTxnGroup.map((txn) => {
      return algosdk.decodeObj(txn);
    });
    decodedObjects.forEach((txnObject, idx) => {
      const isSigned = isSignedTxnObject(txnObject);
      const shouldSign = shouldSignTxnObject(txnObject, this.addresses, indexesToSign, idx);
      const txnBuffer = msgpackTxnGroup[idx];
      const txn = isSigned ? algosdk.decodeSignedTransaction(txnBuffer).txn : algosdk.decodeUnsignedTransaction(txnBuffer);
      const txnBase64 = Buffer.from(txn.toByte()).toString("base64");
      if (shouldSign) {
        txnsToSign.push({ txn: txnBase64 });
        signedIndexes.push(idx);
      } else {
        txnsToSign.push({ txn: txnBase64, signers: [] });
      }
    });
    const signTxnsResult = await this.client.signTxns(txnsToSign);
    const signedTxnsBase64 = signTxnsResult.filter(Boolean);
    const signedTxns = signedTxnsBase64.map((txn) => new Uint8Array(Buffer.from(txn, "base64")));
    const txnGroupSigned = mergeSignedTxnsWithGroup(
      signedTxns,
      msgpackTxnGroup,
      signedIndexes,
      returnGroup
    );
    return txnGroupSigned;
  }
  async transactionSigner(txnGroup, indexesToSign) {
    if (!this.client) {
      throw new Error("[ExodusWallet] Client not initialized!");
    }
    const txnsToSign = txnGroup.reduce((acc, txn, idx) => {
      const txnBase64 = Buffer.from(txn.toByte()).toString("base64");
      if (indexesToSign.includes(idx)) {
        acc.push({ txn: txnBase64 });
      } else {
        acc.push({ txn: txnBase64, signers: [] });
      }
      return acc;
    }, []);
    const signTxnsResult = await this.client.signTxns(txnsToSign);
    const signedTxnsBase64 = signTxnsResult.filter(Boolean);
    const signedTxns = signedTxnsBase64.map((txn) => new Uint8Array(Buffer.from(txn, "base64")));
    return signedTxns;
  }
};

// src/wallets/kmd.ts
import algosdk2 from "algosdk";
var icon2 = "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz48c3ZnIGlkPSJMYXllcl8xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA0MDkuODMgMjEwLjMzIj48dGV4dCB0cmFuc2Zvcm09InRyYW5zbGF0ZSgwIDE2MS4zMSkiIHN0eWxlPSJmb250LWZhbWlseTpJQk1QbGV4U2Fucy1NZWRtLCAmYXBvcztJQk0gUGxleCBTYW5zJmFwb3M7OyBmb250LXNpemU6MTkwcHg7Ij48dHNwYW4geD0iMCIgeT0iMCI+S01EPC90c3Bhbj48L3RleHQ+PC9zdmc+";
var KmdWallet = class extends BaseWallet {
  client = null;
  options;
  walletName;
  walletId = "";
  password = "";
  store;
  constructor({ id, store, subscribe, options, metadata = {} }) {
    super({ id, metadata, store, subscribe });
    const {
      token = "a".repeat(64),
      baseServer = "http://127.0.0.1",
      port = 7833,
      wallet = "unencrypted-default-wallet"
    } = options || {};
    this.options = { token, baseServer, port };
    this.walletName = wallet;
    this.store = store;
  }
  static defaultMetadata = { name: "KMD", icon: icon2 };
  async initializeClient() {
    console.info("[KmdWallet] Initializing client...");
    const { token, baseServer, port } = this.options;
    const client = new algosdk2.Kmd(token, baseServer, port);
    this.client = client;
    return client;
  }
  async connect() {
    console.info("[KmdWallet] Connecting...");
    try {
      if (!this.client) {
        await this.initializeClient();
      }
      const walletId = this.walletId || await this.fetchWalletId();
      const token = await this.fetchToken(walletId, this.getPassword());
      const accounts = await this.fetchAccounts(token);
      if (accounts.length === 0) {
        throw new Error("No accounts found!");
      }
      const walletAccounts = accounts.map((address, idx) => ({
        name: `KMD Wallet ${idx + 1}`,
        address
      }));
      const activeAccount = walletAccounts[0];
      addWallet(this.store, {
        walletId: this.id,
        wallet: {
          accounts: walletAccounts,
          activeAccount
        }
      });
      await this.releaseToken(token);
      return walletAccounts;
    } catch (error) {
      console.error("[KmdWallet] Error connecting:", error);
      return [];
    }
  }
  async disconnect() {
    console.info("[KmdWallet] Disconnecting...");
    this.onDisconnect();
  }
  resumeSession() {
    return Promise.resolve();
  }
  async signTransactions(txnGroup, indexesToSign, returnGroup = true) {
    if (!this.client) {
      throw new Error("[KmdWallet] Client not initialized!");
    }
    const walletId = this.walletId || await this.fetchWalletId();
    const password = this.getPassword();
    const token = await this.fetchToken(walletId, password);
    const signTxnPromises = [];
    const signedIndexes = [];
    const msgpackTxnGroup = normalizeTxnGroup(txnGroup);
    const decodedObjects = msgpackTxnGroup.map((txn) => {
      return algosdk2.decodeObj(txn);
    });
    decodedObjects.forEach((txnObject, idx) => {
      const isSigned = isSignedTxnObject(txnObject);
      const shouldSign = shouldSignTxnObject(txnObject, this.addresses, indexesToSign, idx);
      const txnBuffer = msgpackTxnGroup[idx];
      const txn = isSigned ? algosdk2.decodeSignedTransaction(txnBuffer).txn : algosdk2.decodeUnsignedTransaction(txnBuffer);
      if (shouldSign) {
        signTxnPromises.push(this.client.signTransaction(token, password, txn));
        signedIndexes.push(idx);
      }
    });
    const signedTxns = await Promise.all(signTxnPromises);
    await this.releaseToken(token);
    const txnGroupSigned = mergeSignedTxnsWithGroup(
      signedTxns,
      msgpackTxnGroup,
      signedIndexes,
      returnGroup
    );
    return txnGroupSigned;
  }
  async transactionSigner(txnGroup, indexesToSign) {
    if (!this.client) {
      throw new Error("[KmdWallet] Client not initialized!");
    }
    const walletId = this.walletId || await this.fetchWalletId();
    const password = this.getPassword();
    const token = await this.fetchToken(walletId, password);
    const signTxnPromises = [];
    txnGroup.forEach((txn, idx) => {
      if (indexesToSign.includes(idx)) {
        signTxnPromises.push(this.client.signTransaction(token, password, txn));
      }
    });
    const signedTxns = await Promise.all(signTxnPromises);
    return signedTxns;
  }
  async fetchWalletId() {
    console.info("[KmdWallet] Fetching wallet data...");
    if (!this.client) {
      throw new Error("Client not initialized!");
    }
    const { wallets } = await this.client.listWallets();
    const wallet = wallets.find((wallet2) => wallet2.name === this.walletName);
    if (!wallet) {
      throw new Error(`Wallet ${this.walletName} not found!`);
    }
    this.walletId = wallet.id;
    return wallet.id;
  }
  async fetchToken(walletId, password) {
    console.info("[KmdWallet] Fetching token...");
    if (!this.client) {
      throw new Error("Client not initialized!");
    }
    const { wallet_handle_token } = await this.client.initWalletHandle(
      walletId,
      password
    );
    return wallet_handle_token;
  }
  async fetchAccounts(token) {
    console.info("[KmdWallet] Fetching accounts...");
    if (!this.client) {
      throw new Error("Client not initialized!");
    }
    const { addresses } = await this.client.listKeys(token);
    return addresses;
  }
  async releaseToken(token) {
    console.info("[KmdWallet] Releasing token...");
    if (!this.client) {
      throw new Error("Client not initialized!");
    }
    await this.client.releaseWalletHandle(token);
  }
  getPassword() {
    if (this.password) {
      return this.password;
    }
    const password = prompt("KMD password") || "";
    this.password = password;
    return password;
  }
};

// src/wallets/mnemonic.ts
import algosdk3 from "algosdk";
var icon3 = `data:image/svg+xml,%3c%3fxml version='1.0' encoding='UTF-8'%3f%3e %3c!-- Generated by Pixelmator Pro 3.2.2 --%3e %3csvg width='409' height='210' viewBox='0 0 409 210' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3e%3ctext id='MNEMONIC' xml:space='preserve' x='0' y='129' font-family='Helvetica' font-size='72' fill='black'%3eMNEMONIC%3c/text%3e%3c/svg%3e`;
var MnemonicWallet = class extends BaseWallet {
  account = null;
  options;
  store;
  constructor({
    id,
    store,
    subscribe,
    options,
    metadata = {}
  }) {
    super({ id, metadata, store, subscribe });
    const { persistToStorage = false } = options || {};
    this.options = { persistToStorage };
    this.store = store;
  }
  static defaultMetadata = { name: "Mnemonic", icon: icon3 };
  // @todo: Show explicit security warning if persistToStorage is true
  // @todo: Save/load mnemonic from storage if persistToStorage is true
  // @todo: Throw error with link to docs if using mainnet
  initializeAccount() {
    const mnemonic = prompt("Enter 25-word mnemonic passphrase:");
    if (!mnemonic) {
      this.account = null;
      throw new Error("No mnemonic provided");
    }
    const account = algosdk3.mnemonicToSecretKey(mnemonic);
    this.account = account;
    return account;
  }
  async connect() {
    console.info("[MnemonicWallet] Connecting...");
    try {
      const account = this.initializeAccount();
      const walletAccount = {
        name: "Mnemonic Account",
        address: account.addr
      };
      addWallet(this.store, {
        walletId: this.id,
        wallet: {
          accounts: [walletAccount],
          activeAccount: walletAccount
        }
      });
      return [walletAccount];
    } catch (error) {
      console.error("[MnemonicWallet] Error connecting:", error);
      throw error;
    }
  }
  async disconnect() {
    console.info("[MnemonicWallet] Disconnecting...");
    try {
      this.account = null;
      this.onDisconnect();
    } catch (error) {
      console.error(error);
    }
  }
  async resumeSession() {
    const state = this.store.state;
    const walletState = state.wallets[this.id];
    if (walletState) {
      try {
        this.account = null;
        this.onDisconnect();
      } catch (error) {
        console.error(error);
      }
    }
  }
  async signTransactions(txnGroup, indexesToSign, returnGroup = true) {
    if (!this.account) {
      throw new Error("[MnemonicWallet] Client not initialized!");
    }
    const txnGroupSigned = [];
    const msgpackTxnGroup = normalizeTxnGroup(txnGroup);
    const decodedObjects = msgpackTxnGroup.map((txn) => {
      return algosdk3.decodeObj(txn);
    });
    decodedObjects.forEach((txnObject, idx) => {
      const isIndexMatch = !indexesToSign || indexesToSign.includes(idx);
      const isSigned = isSignedTxnObject(txnObject);
      const canSign = !isSigned && algosdk3.encodeAddress(txnObject.snd) === this.account.addr;
      const shouldSign = isIndexMatch && canSign;
      if (shouldSign) {
        const txn = algosdk3.Transaction.from_obj_for_encoding(txnObject);
        const signedTxn = txn.signTxn(this.account.sk);
        txnGroupSigned.push(signedTxn);
      } else if (returnGroup) {
        txnGroupSigned.push(msgpackTxnGroup[idx]);
      }
    });
    return Promise.resolve(txnGroupSigned);
  }
  async transactionSigner(txnGroup, indexesToSign) {
    if (!this.account) {
      throw new Error("[MnemonicWallet] Account not initialized!");
    }
    const signedTxns = [];
    for (const index of indexesToSign) {
      const txnToSign = txnGroup[index];
      if (txnToSign) {
        signedTxns.push(txnToSign.signTxn(this.account.sk));
      }
    }
    return Promise.resolve(signedTxns);
  }
};

// src/wallets/pera.ts
import algosdk4 from "algosdk";
var icon4 = "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz48c3ZnIGlkPSJMYXllcl8xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxNzcgMTg3Ij48cmVjdCB4PSItMTEuMzgiIHk9Ii0yNS45NyIgd2lkdGg9IjIwMC4wMiIgaGVpZ2h0PSIyMzEuNTMiIHN0eWxlPSJmaWxsOiNmZTU7Ii8+PHBhdGggZD0iTTk0LjA1LDU5LjYxYzIuMDUsOC40OCwxLjM2LDE1Ljk0LTEuNTUsMTYuNjYtMi45LC43Mi02LjkxLTUuNTctOC45Ni0xNC4wNS0yLjA1LTguNDgtMS4zNi0xNS45NCwxLjU1LTE2LjY2LDIuOS0uNzIsNi45MSw1LjU3LDguOTYsMTQuMDVaIiBzdHlsZT0iZmlsbDojMWMxYzFjOyIvPjxwYXRoIGQ9Ik0xMjcuODUsNjYuOWMtNC41My00LjgxLTEzLjU1LTMuNS0yMC4xNSwyLjkxLTYuNTksNi40MS04LjI2LDE1LjUtMy43MywyMC4zMSw0LjUzLDQuOCwxMy41NSwzLjUsMjAuMTUtMi45MXM4LjI2LTE1LjUsMy43My0yMC4zMVoiIHN0eWxlPSJmaWxsOiMxYzFjMWM7Ii8+PHBhdGggZD0iTTkxLjc5LDE0MC40N2MyLjktLjcyLDMuNDktOC42LDEuMzItMTcuNjEtMi4xNy05LTYuMjktMTUuNzEtOS4xOS0xNC45OS0yLjksLjcyLTMuNDksOC42LTEuMzIsMTcuNjEsMi4xNyw5LDYuMjksMTUuNzEsOS4xOSwxNC45OVoiIHN0eWxlPSJmaWxsOiMxYzFjMWM7Ii8+PHBhdGggZD0iTTYyLjIyLDcxLjNjOC4zNywyLjQ3LDE0LjQ4LDYuOCwxMy42Niw5LjY3LS44MywyLjg3LTguMjgsMy4yLTE2LjY1LC43My04LjM3LTIuNDctMTQuNDgtNi44LTEzLjY2LTkuNjcsLjgzLTIuODcsOC4yOC0zLjIsMTYuNjUtLjczWiIgc3R5bGU9ImZpbGw6IzFjMWMxYzsiLz48cGF0aCBkPSJNMTE2LjU0LDEwMy43NGM4Ljg4LDIuNjIsMTUuNDEsNy4wNywxNC41OSw5Ljk0LS44MywyLjg3LTguNywzLjA4LTE3LjU4LC40Ni04Ljg4LTIuNjItMTUuNDEtNy4wNy0xNC41OS05Ljk0LC44My0yLjg3LDguNy0zLjA4LDE3LjU4LS40NloiIHN0eWxlPSJmaWxsOiMxYzFjMWM7Ii8+PHBhdGggZD0iTTcxLjY0LDk3LjcxYy0yLjA4LTIuMTUtOC44OCwuOTgtMTUuMiw2Ljk5LTYuMzIsNi4wMS05Ljc2LDEyLjYzLTcuNjksMTQuNzgsMi4wOCwyLjE1LDguODgtLjk4LDE1LjItNi45OSw2LjMyLTYuMDEsOS43Ni0xMi42Myw3LjY5LTE0Ljc4WiIgc3R5bGU9ImZpbGw6IzFjMWMxYzsiLz48L3N2Zz4=";
var PeraWallet = class extends BaseWallet {
  client = null;
  options;
  store;
  constructor({
    id,
    store,
    subscribe,
    options = {},
    metadata = {}
  }) {
    super({ id, metadata, store, subscribe });
    this.options = options;
    this.store = store;
  }
  static defaultMetadata = { name: "Pera", icon: icon4 };
  async initializeClient() {
    console.info("[PeraWallet] Initializing client...");
    const module = await import("@perawallet/connect");
    const PeraWalletConnect = module.default ? module.default.PeraWalletConnect : module.PeraWalletConnect;
    const client = new PeraWalletConnect(this.options);
    client.connector?.on("disconnect", this.onDisconnect);
    this.client = client;
    return client;
  }
  async connect() {
    console.info("[PeraWallet] Connecting...");
    try {
      const client = this.client || await this.initializeClient();
      const accounts = await client.connect();
      if (accounts.length === 0) {
        throw new Error("No accounts found!");
      }
      const walletAccounts = accounts.map((address, idx) => ({
        name: `Pera Wallet ${idx + 1}`,
        address
      }));
      const activeAccount = walletAccounts[0];
      addWallet(this.store, {
        walletId: this.id,
        wallet: {
          accounts: walletAccounts,
          activeAccount
        }
      });
      return walletAccounts;
    } catch (error) {
      if (error?.data?.type !== "CONNECT_MODAL_CLOSED") {
        console.error(`[PeraWallet] Error connecting: ${error.message}`);
      } else {
        console.info("[PeraWallet] Connection cancelled.");
      }
      return [];
    }
  }
  async disconnect() {
    console.info("[PeraWallet] Disconnecting...");
    try {
      await this.client?.disconnect();
      this.onDisconnect();
    } catch (error) {
      console.error(error);
    }
  }
  async resumeSession() {
    try {
      const state = this.store.state;
      const walletState = state.wallets[this.id];
      if (!walletState) {
        return;
      }
      console.info("[PeraWallet] Resuming session...");
      const client = this.client || await this.initializeClient();
      const accounts = await client.reconnectSession();
      if (accounts.length === 0) {
        throw new Error("[PeraWallet] No accounts found!");
      }
      const walletAccounts = accounts.map((address, idx) => ({
        name: `Pera Wallet ${idx + 1}`,
        address
      }));
      const match = compareAccounts(walletAccounts, walletState.accounts);
      if (!match) {
        console.warn(`[PeraWallet] Session accounts mismatch, updating accounts`);
        setAccounts(this.store, {
          walletId: this.id,
          accounts: walletAccounts
        });
      }
    } catch (error) {
      console.error(error);
      this.onDisconnect();
    }
  }
  async signTransactions(txnGroup, indexesToSign, returnGroup = true) {
    if (!this.client) {
      throw new Error("[PeraWallet] Client not initialized!");
    }
    const txnsToSign = [];
    const signedIndexes = [];
    const msgpackTxnGroup = normalizeTxnGroup(txnGroup);
    const decodedObjects = msgpackTxnGroup.map((txn) => {
      return algosdk4.decodeObj(txn);
    });
    decodedObjects.forEach((txnObject, idx) => {
      const isSigned = isSignedTxnObject(txnObject);
      const shouldSign = shouldSignTxnObject(txnObject, this.addresses, indexesToSign, idx);
      const txnBuffer = msgpackTxnGroup[idx];
      const txn = isSigned ? algosdk4.decodeSignedTransaction(txnBuffer).txn : algosdk4.decodeUnsignedTransaction(txnBuffer);
      if (shouldSign) {
        txnsToSign.push({ txn });
        signedIndexes.push(idx);
      } else {
        txnsToSign.push({ txn, signers: [] });
      }
    });
    const signedTxns = await this.client.signTransaction([txnsToSign]);
    const txnGroupSigned = mergeSignedTxnsWithGroup(
      signedTxns,
      msgpackTxnGroup,
      signedIndexes,
      returnGroup
    );
    return txnGroupSigned;
  }
  async transactionSigner(txnGroup, indexesToSign) {
    if (!this.client) {
      throw new Error("[PeraWallet] Client not initialized!");
    }
    const txnsToSign = txnGroup.reduce((acc, txn, idx) => {
      if (indexesToSign.includes(idx)) {
        acc.push({ txn });
      } else {
        acc.push({ txn, signers: [] });
      }
      return acc;
    }, []);
    const signTxnsResult = await this.client.signTransaction([txnsToSign]);
    return signTxnsResult;
  }
};

// src/wallets/walletconnect.ts
import { getAppMetadata, getSdkError } from "@walletconnect/utils";
import algosdk5 from "algosdk";
var icon5 = "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPHN2ZyB2ZXJzaW9uPSIxLjEiIGlkPSJMYXllcl8xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB4PSIwcHgiIHk9IjBweCIKCSB2aWV3Qm94PSIwIDAgNDgwIDQ4MCIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgNDgwIDQ4MDsiIHhtbDpzcGFjZT0icHJlc2VydmUiPgo8c3R5bGUgdHlwZT0idGV4dC9jc3MiPgoJLnN0MHtmaWxsOiMzMzk2RkY7fQo8L3N0eWxlPgo8cGF0aCBjbGFzcz0ic3QwIiBkPSJNMTI2LjYsMTY4YzYyLjYtNjEuMywxNjQuMi02MS4zLDIyNi44LDBsNy41LDcuNGMzLjEsMy4xLDMuMSw4LDAsMTEuMWwtMjUuOCwyNS4yYy0xLjYsMS41LTQuMSwxLjUtNS43LDAKCWwtMTAuNC0xMC4yYy00My43LTQyLjgtMTE0LjUtNDIuOC0xNTguMiwwbC0xMS4xLDEwLjljLTEuNiwxLjUtNC4xLDEuNS01LjcsMGwtMjUuOC0yNS4yYy0zLjEtMy4xLTMuMS04LDAtMTEuMUwxMjYuNiwxNjh6CgkgTTQwNi43LDIyMC4ybDIyLjksMjIuNWMzLjEsMy4xLDMuMSw4LDAsMTEuMUwzMjYuMiwzNTUuMWMtMy4xLDMuMS04LjIsMy4xLTExLjMsMGwtNzMuNC03MS45Yy0wLjgtMC44LTIuMS0wLjgtMi44LDBsLTczLjQsNzEuOQoJYy0zLjEsMy4xLTguMiwzLjEtMTEuMywwTDUwLjMsMjUzLjhjLTMuMS0zLjEtMy4xLTgsMC0xMS4xbDIyLjktMjIuNWMzLjEtMy4xLDguMi0zLjEsMTEuMywwbDczLjQsNzEuOWMwLjgsMC44LDIuMSwwLjgsMi44LDAKCWw3My40LTcxLjljMy4xLTMuMSw4LjItMy4xLDExLjMsMGw3My40LDcxLjljMC44LDAuOCwyLjEsMC44LDIuOCwwbDczLjQtNzEuOUMzOTguNSwyMTcuMSw0MDMuNiwyMTcuMSw0MDYuNywyMjAuMkw0MDYuNywyMjAuMnoiLz4KPC9zdmc+Cg==";
var WalletConnect = class extends BaseWallet {
  client = null;
  options;
  modal = null;
  modalOptions;
  session = null;
  chains;
  store;
  constructor({
    id,
    store,
    subscribe,
    options,
    metadata = {}
  }) {
    super({ id, metadata, store, subscribe });
    if (!options) {
      throw new Error("[WalletConnect] Options are required.");
    }
    const {
      projectId,
      relayUrl = "wss://relay.walletconnect.com",
      metadata: _metadata = getAppMetadata(),
      ...modalOptions
    } = options;
    this.options = {
      projectId,
      relayUrl,
      ..._metadata
    };
    this.modalOptions = modalOptions;
    this.chains = Object.values(caipChainId);
    this.store = store;
  }
  static defaultMetadata = { name: "WalletConnect", icon: icon5 };
  async initializeClient() {
    console.info("[WalletConnect] Initializing client...");
    const SignClient = (await import("@walletconnect/sign-client")).SignClient;
    const client = await SignClient.init(this.options);
    client.on("session_event", (args) => {
      console.log("[WalletConnect] EVENT", "session_event", args);
    });
    client.on("session_update", ({ topic, params }) => {
      console.log("[WalletConnect] EVENT", "session_update", { topic, params });
      const { namespaces } = params;
      const session = client.session.get(topic);
      const updatedSession = { ...session, namespaces };
      this.onSessionConnected(updatedSession);
    });
    client.on("session_delete", () => {
      console.log("[WalletConnect] EVENT", "session_delete");
      this.session = null;
    });
    this.client = client;
    return client;
  }
  async initializeModal() {
    console.info("[WalletConnect] Initializing modal...");
    const WalletConnectModal = (await import("@walletconnect/modal")).WalletConnectModal;
    const modal = new WalletConnectModal({
      projectId: this.options.projectId,
      chains: this.chains,
      ...this.modalOptions
    });
    modal.subscribeModal(
      (state) => console.info(`[WalletConnect] Modal ${state.open ? "open" : "closed"}`)
    );
    this.modal = modal;
    return modal;
  }
  onSessionConnected(session) {
    const caipAccounts = session.namespaces.algorand.accounts;
    if (!caipAccounts.length) {
      throw new Error("No accounts found!");
    }
    const accounts = [...new Set(caipAccounts.map((account) => account.split(":").pop()))];
    const walletAccounts = accounts.map((address, idx) => ({
      name: `WalletConnect ${idx + 1}`,
      address
    }));
    const state = this.store.state;
    const walletState = state.wallets[this.id];
    if (!walletState) {
      addWallet(this.store, {
        walletId: this.id,
        wallet: {
          accounts: walletAccounts,
          activeAccount: walletAccounts[0]
        }
      });
    } else {
      const match = compareAccounts(walletAccounts, walletState.accounts);
      if (!match) {
        console.warn(`[WalletConnect] Session accounts mismatch, updating accounts`);
        setAccounts(this.store, {
          walletId: this.id,
          accounts: walletAccounts
        });
      }
    }
    this.session = session;
    return walletAccounts;
  }
  async connect() {
    console.info("[WalletConnect] Connecting...");
    try {
      const client = this.client || await this.initializeClient();
      const modal = this.modal || await this.initializeModal();
      const requiredNamespaces = {
        algorand: {
          chains: this.chains,
          methods: ["algo_signTxn"],
          events: []
        }
      };
      const { uri, approval } = await client.connect({ requiredNamespaces });
      if (!uri) {
        throw new Error("No URI found");
      }
      await modal.openModal({ uri });
      const session = await approval();
      const walletAccounts = this.onSessionConnected(session);
      return walletAccounts;
    } catch (error) {
      console.error(`[WalletConnect] Error connecting: ${error.message}`);
      return [];
    } finally {
      this.modal?.closeModal();
    }
  }
  async disconnect() {
    console.info("[WalletConnect] Disconnecting...");
    try {
      if (this.client && this.session) {
        await this.client.disconnect({
          topic: this.session.topic,
          reason: getSdkError("USER_DISCONNECTED")
        });
      }
      this.onDisconnect();
    } catch (error) {
      console.error(error);
    }
  }
  async resumeSession() {
    try {
      const state = this.store.state;
      const walletState = state.wallets[this.id];
      if (!walletState) {
        return;
      }
      console.info("[WalletConnect] Resuming session...");
      const client = this.client || await this.initializeClient();
      if (client.session.length) {
        const lastKeyIndex = client.session.keys.length - 1;
        const restoredSession = client.session.get(client.session.keys[lastKeyIndex]);
        this.onSessionConnected(restoredSession);
      }
    } catch (error) {
      console.error(error);
      this.onDisconnect();
    }
  }
  async signTransactions(txnGroup, indexesToSign, returnGroup = true) {
    if (!this.client) {
      throw new Error("[WalletConnect] Client not initialized!");
    }
    if (!this.session) {
      throw new Error("[WalletConnect] Session is not connected");
    }
    if (this.activeNetwork === "localnet" /* LOCALNET */) {
      throw new Error(`[WalletConnect] Invalid network: ${this.activeNetwork}`);
    }
    const txnsToSign = [];
    const signedIndexes = [];
    const msgpackTxnGroup = normalizeTxnGroup(txnGroup);
    const decodedObjects = msgpackTxnGroup.map((txn) => {
      return algosdk5.decodeObj(txn);
    });
    decodedObjects.forEach((txnObject, idx) => {
      const isSigned = isSignedTxnObject(txnObject);
      const shouldSign = shouldSignTxnObject(txnObject, this.addresses, indexesToSign, idx);
      const txnBuffer = msgpackTxnGroup[idx];
      const txn = isSigned ? algosdk5.decodeSignedTransaction(txnBuffer).txn : algosdk5.decodeUnsignedTransaction(txnBuffer);
      const txnBase64 = Buffer.from(txn.toByte()).toString("base64");
      if (shouldSign) {
        txnsToSign.push({ txn: txnBase64 });
        signedIndexes.push(idx);
      } else {
        txnsToSign.push({ txn: txnBase64, signers: [] });
      }
    });
    const request = formatJsonRpcRequest("algo_signTxn", [txnsToSign]);
    const signTxnsResult = await this.client.request({
      chainId: caipChainId[this.activeNetwork],
      topic: this.session.topic,
      request
    });
    const signedTxnsBase64 = signTxnsResult.filter(Boolean);
    const signedTxns = signedTxnsBase64.map((txn) => new Uint8Array(Buffer.from(txn, "base64")));
    const txnGroupSigned = mergeSignedTxnsWithGroup(
      signedTxns,
      msgpackTxnGroup,
      signedIndexes,
      returnGroup
    );
    return txnGroupSigned;
  }
  async transactionSigner(txnGroup, indexesToSign) {
    if (!this.client) {
      throw new Error("[WalletConnect] Client not initialized!");
    }
    if (!this.session) {
      throw new Error("[WalletConnect] Session is not connected");
    }
    if (this.activeNetwork === "localnet" /* LOCALNET */) {
      throw new Error(`[WalletConnect] Invalid network: ${this.activeNetwork}`);
    }
    const txnsToSign = txnGroup.reduce((acc, txn, idx) => {
      const txnBase64 = Buffer.from(txn.toByte()).toString("base64");
      if (indexesToSign.includes(idx)) {
        acc.push({ txn: txnBase64 });
      } else {
        acc.push({ txn: txnBase64, signers: [] });
      }
      return acc;
    }, []);
    const request = formatJsonRpcRequest("algo_signTxn", [txnsToSign]);
    const signTxnsResult = await this.client.request({
      chainId: caipChainId[this.activeNetwork],
      topic: this.session.topic,
      request
    });
    const signedTxnsBase64 = signTxnsResult.filter(Boolean);
    const signedTxns = signedTxnsBase64.map((txn) => new Uint8Array(Buffer.from(txn, "base64")));
    return signedTxns;
  }
};

// src/utils.ts
function createWalletMap() {
  return {
    ["defly" /* DEFLY */]: DeflyWallet,
    ["exodus" /* EXODUS */]: ExodusWallet,
    ["kmd" /* KMD */]: KmdWallet,
    ["mnemonic" /* MNEMONIC */]: MnemonicWallet,
    ["pera" /* PERA */]: PeraWallet,
    ["walletconnect" /* WALLETCONNECT */]: WalletConnect
  };
}
function compareAccounts(accounts, compareTo) {
  const addresses = new Set(accounts.map((account) => account.address));
  const compareAddresses = new Set(compareTo.map((account) => account.address));
  if (addresses.size !== compareAddresses.size) {
    return false;
  }
  for (const address of addresses) {
    if (!compareAddresses.has(address)) {
      return false;
    }
  }
  return true;
}
function isTransaction(item) {
  if (Array.isArray(item)) {
    return item.every(
      (elem) => typeof elem === "object" && elem !== null && "genesisID" in elem && typeof elem.genesisID === "string"
    );
  } else {
    return typeof item === "object" && item !== null && "genesisID" in item && typeof item.genesisID === "string";
  }
}
function isSignedTxnObject(item) {
  return item.txn !== void 0;
}
function normalizeTxnGroup(txnGroup) {
  if (!txnGroup[0]) {
    throw new Error("Empty transaction group!");
  }
  const isTransactionType = isTransaction(txnGroup[0]);
  if (isTransactionType) {
    const transactionGroup = Array.isArray(txnGroup[0]) ? txnGroup.flatMap((txn) => txn) : txnGroup;
    return transactionGroup.map((txn) => {
      return algosdk6.encodeUnsignedTransaction(txn);
    });
  } else {
    const transactionGroup = Array.isArray(txnGroup[0]) ? txnGroup.flatMap((txn) => txn) : txnGroup;
    return transactionGroup;
  }
}
function shouldSignTxnObject(txnObject, addresses, indexesToSign, idx) {
  const isIndexMatch = !indexesToSign || indexesToSign.includes(idx);
  const isSigned = isSignedTxnObject(txnObject);
  const canSign = !isSigned && addresses.includes(algosdk6.encodeAddress(txnObject.snd));
  const shouldSign = isIndexMatch && canSign;
  return shouldSign;
}
function mergeSignedTxnsWithGroup(signedTxns, txnGroup, signedIndexes, returnGroup) {
  return txnGroup.reduce((acc, txn, i) => {
    if (signedIndexes.includes(i)) {
      const signedByUser = signedTxns.shift();
      signedByUser && acc.push(signedByUser);
    } else if (returnGroup) {
      acc.push(txnGroup[i]);
    }
    return acc;
  }, []);
}
function getPayloadId() {
  const date = Date.now() * Math.pow(10, 3);
  const extra = Math.floor(Math.random() * Math.pow(10, 3));
  return date + extra;
}
function formatJsonRpcRequest(method, params) {
  return {
    id: getPayloadId(),
    jsonrpc: "2.0",
    method,
    params
  };
}
function deepMerge(target, source) {
  const isObject = (obj) => obj && typeof obj === "object";
  if (!isObject(target) || !isObject(source)) {
    throw new Error("Target and source must be objects");
  }
  Object.keys(source).forEach((key) => {
    const targetValue = target[key];
    const sourceValue = source[key];
    if (Array.isArray(targetValue) && Array.isArray(sourceValue)) {
      target[key] = targetValue.concat(sourceValue);
    } else if (isObject(targetValue) && isObject(sourceValue)) {
      target[key] = deepMerge(Object.assign({}, targetValue), sourceValue);
    } else {
      target[key] = sourceValue;
    }
  });
  return target;
}

// src/wallets/defly.ts
var icon6 = "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiIHN0YW5kYWxvbmU9Im5vIj8+CjwhRE9DVFlQRSBzdmcgUFVCTElDICItLy9XM0MvL0RURCBTVkcgMS4xLy9FTiIgImh0dHA6Ly93d3cudzMub3JnL0dyYXBoaWNzL1NWRy8xLjEvRFREL3N2ZzExLmR0ZCI+Cjxzdmcgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdmlld0JveD0iMCAwIDEwMjQgMTAyNCIgdmVyc2lvbj0iMS4xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB4bWw6c3BhY2U9InByZXNlcnZlIiB4bWxuczpzZXJpZj0iaHR0cDovL3d3dy5zZXJpZi5jb20vIiBzdHlsZT0iZmlsbC1ydWxlOmV2ZW5vZGQ7Y2xpcC1ydWxlOmV2ZW5vZGQ7c3Ryb2tlLWxpbmVqb2luOnJvdW5kO3N0cm9rZS1taXRlcmxpbWl0OjI7Ij4KICAgIDxyZWN0IHg9IjAiIHk9IjAiIHdpZHRoPSIxMDI0IiBoZWlnaHQ9IjEwMjQiLz4KICAgIDxnIHRyYW5zZm9ybT0ibWF0cml4KDEuNjgyMDksMCwwLDEuNjgyMDksMjI2LjM2OCwyMTIuODE4KSI+CiAgICAgICAgPHBhdGggZD0iTTMyNy4wNDksMjgwLjE5MkwxNjkuNTI0LDEzTDEyLDI4MC4xOTJMMTY5LjUyNCwxODkuMDg0TDMyNy4wNDksMjgwLjE5MloiIHN0eWxlPSJmaWxsOndoaXRlO2ZpbGwtcnVsZTpub256ZXJvOyIvPgogICAgPC9nPgogICAgPGcgdHJhbnNmb3JtPSJtYXRyaXgoMS42ODIwOSwwLDAsMS42ODIwOSwyMjYuMzY4LDIxMi44MTgpIj4KICAgICAgICA8cGF0aCBkPSJNMjk5LjU0NiwzMDdMMTY5LjUyNSwyMzguNDczTDM5LjUwNCwzMDdMMTY5LjUyNSwyNjQuNjdMMjk5LjU0NiwzMDdaIiBzdHlsZT0iZmlsbDp3aGl0ZTtmaWxsLXJ1bGU6bm9uemVybzsiLz4KICAgIDwvZz4KPC9zdmc+Cg==";
var DeflyWallet = class extends BaseWallet {
  client = null;
  options;
  store;
  constructor({
    id,
    store,
    subscribe,
    options = {},
    metadata = {}
  }) {
    super({ id, metadata, store, subscribe });
    this.options = options;
    this.store = store;
  }
  static defaultMetadata = { name: "Defly", icon: icon6 };
  async initializeClient() {
    console.info("[DeflyWallet] Initializing client...");
    const module = await import("@blockshake/defly-connect");
    const DeflyWalletConnect = module.default ? module.default.DeflyWalletConnect : module.DeflyWalletConnect;
    const client = new DeflyWalletConnect(this.options);
    client.connector?.on("disconnect", this.onDisconnect);
    this.client = client;
    return client;
  }
  async connect() {
    console.info("[DeflyWallet] Connecting...");
    try {
      const client = this.client || await this.initializeClient();
      const accounts = await client.connect();
      if (accounts.length === 0) {
        throw new Error("No accounts found!");
      }
      const walletAccounts = accounts.map((address, idx) => ({
        name: `Defly Wallet ${idx + 1}`,
        address
      }));
      const activeAccount = walletAccounts[0];
      addWallet(this.store, {
        walletId: this.id,
        wallet: {
          accounts: walletAccounts,
          activeAccount
        }
      });
      return walletAccounts;
    } catch (error) {
      if (error?.data?.type !== "CONNECT_MODAL_CLOSED") {
        console.error(`[DeflyWallet] Error connecting: ${error.message}`);
      } else {
        console.info("[DeflyWallet] Connection cancelled.");
      }
      return [];
    }
  }
  async disconnect() {
    console.info("[DeflyWallet] Disconnecting...");
    try {
      await this.client?.disconnect();
      this.onDisconnect();
    } catch (error) {
      console.error(error);
    }
  }
  async resumeSession() {
    try {
      const state = this.store.state;
      const walletState = state.wallets[this.id];
      if (!walletState) {
        return;
      }
      console.info("[DeflyWallet] Resuming session...");
      const client = this.client || await this.initializeClient();
      const accounts = await client.reconnectSession();
      if (accounts.length === 0) {
        throw new Error("[DeflyWallet] No accounts found!");
      }
      const walletAccounts = accounts.map((address, idx) => ({
        name: `Defly Wallet ${idx + 1}`,
        address
      }));
      const match = compareAccounts(walletAccounts, walletState.accounts);
      if (!match) {
        console.warn(`[DeflyWallet] Session accounts mismatch, updating accounts`);
        setAccounts(this.store, {
          walletId: this.id,
          accounts: walletAccounts
        });
      }
    } catch (error) {
      console.error(error);
      this.onDisconnect();
    }
  }
  async signTransactions(txnGroup, indexesToSign, returnGroup = true) {
    if (!this.client) {
      throw new Error("[DeflyWallet] Client not initialized!");
    }
    const txnsToSign = [];
    const signedIndexes = [];
    const msgpackTxnGroup = normalizeTxnGroup(txnGroup);
    const decodedObjects = msgpackTxnGroup.map((txn) => {
      return algosdk7.decodeObj(txn);
    });
    decodedObjects.forEach((txnObject, idx) => {
      const isSigned = isSignedTxnObject(txnObject);
      const shouldSign = shouldSignTxnObject(txnObject, this.addresses, indexesToSign, idx);
      const txnBuffer = msgpackTxnGroup[idx];
      const txn = isSigned ? algosdk7.decodeSignedTransaction(txnBuffer).txn : algosdk7.decodeUnsignedTransaction(txnBuffer);
      if (shouldSign) {
        txnsToSign.push({ txn });
        signedIndexes.push(idx);
      } else {
        txnsToSign.push({ txn, signers: [] });
      }
    });
    const signedTxns = await this.client.signTransaction([txnsToSign]);
    const txnGroupSigned = mergeSignedTxnsWithGroup(
      signedTxns,
      msgpackTxnGroup,
      signedIndexes,
      returnGroup
    );
    return txnGroupSigned;
  }
  async transactionSigner(txnGroup, indexesToSign) {
    if (!this.client) {
      throw new Error("[DeflyWallet] Client not initialized!");
    }
    const txnsToSign = txnGroup.reduce((acc, txn, idx) => {
      if (indexesToSign.includes(idx)) {
        acc.push({ txn });
      } else {
        acc.push({ txn, signers: [] });
      }
      return acc;
    }, []);
    const signTxnsResult = await this.client.signTransaction([txnsToSign]);
    return signTxnsResult;
  }
};

// src/store.ts
var defaultState = {
  wallets: {},
  activeWallet: null,
  activeNetwork: "testnet" /* TESTNET */
};
var LOCAL_STORAGE_KEY = "@txnlab/use-wallet-js";
function addWallet(store, { walletId, wallet }) {
  store.setState((state) => {
    const newWallets = {
      ...state.wallets,
      [walletId]: wallet
    };
    return {
      ...state,
      wallets: newWallets,
      activeWallet: walletId
    };
  });
}
function removeWallet(store, { walletId }) {
  store.setState((state) => {
    const newWallets = { ...state.wallets };
    delete newWallets[walletId];
    return {
      ...state,
      wallets: newWallets,
      activeWallet: state.activeWallet === walletId ? null : state.activeWallet
    };
  });
}
function setActiveWallet(store, { walletId }) {
  store.setState((state) => {
    return {
      ...state,
      activeWallet: walletId
    };
  });
}
function setActiveAccount(store, { walletId, address }) {
  store.setState((state) => {
    const wallet = state.wallets[walletId];
    if (!wallet) {
      return state;
    }
    const activeAccount = wallet.accounts.find((a) => a.address === address);
    if (!activeAccount) {
      return state;
    }
    const newWallets = {
      ...state.wallets,
      [walletId]: {
        ...wallet,
        activeAccount
      }
    };
    return {
      ...state,
      wallets: newWallets
    };
  });
}
function setAccounts(store, { walletId, accounts }) {
  store.setState((state) => {
    const wallet = state.wallets[walletId];
    if (!wallet) {
      return state;
    }
    const isActiveAccountConnected = accounts.some(
      (account) => account.address === wallet.activeAccount?.address
    );
    const activeAccount = isActiveAccountConnected ? wallet.activeAccount : accounts[0] || null;
    const newWallet = {
      ...wallet,
      accounts,
      activeAccount
    };
    const newWallets = {
      ...state.wallets,
      [walletId]: newWallet
    };
    return {
      ...state,
      wallets: newWallets
    };
  });
}
function setActiveNetwork(store, { networkId }) {
  store.setState((state) => {
    return {
      ...state,
      activeNetwork: networkId
    };
  });
}
function isValidWalletId(walletId) {
  return Object.values(WalletId).includes(walletId);
}
function isValidWalletAccount(account) {
  return typeof account === "object" && account !== null && typeof account.name === "string" && typeof account.address === "string";
}
function isValidWalletState(wallet) {
  return typeof wallet === "object" && wallet !== null && Array.isArray(wallet.accounts) && wallet.accounts.every(isValidWalletAccount) && (wallet.activeAccount === null || isValidWalletAccount(wallet.activeAccount));
}
function isValidState(state) {
  if (!state || typeof state !== "object")
    return false;
  if (typeof state.wallets !== "object")
    return false;
  for (const [walletId, wallet] of Object.entries(state.wallets)) {
    if (!isValidWalletId(walletId) || !isValidWalletState(wallet))
      return false;
  }
  if (state.activeWallet !== null && !isValidWalletId(state.activeWallet))
    return false;
  if (!isValidNetworkId(state.activeNetwork))
    return false;
  return true;
}

// src/manager.ts
var WalletManager = class {
  _clients = /* @__PURE__ */ new Map();
  networkConfig;
  algodClient;
  store;
  subscribe;
  constructor({ wallets = [], network = "testnet" /* TESTNET */, algod = {} } = {}) {
    const initialState = this.loadPersistedState() || {
      ...defaultState,
      activeNetwork: network
    };
    this.store = new Store(initialState, {
      onUpdate: () => this.savePersistedState()
    });
    this.savePersistedState();
    this.subscribe = (callback) => {
      const unsubscribe = this.store.subscribe(() => {
        callback(this.store.state);
      });
      return unsubscribe;
    };
    this.networkConfig = this.initNetworkConfig(network, algod);
    this.algodClient = this.createAlgodClient(this.networkConfig[network]);
    this.initializeWallets(wallets);
  }
  // ---------- Store ------------------------------------------------- //
  loadPersistedState() {
    try {
      const serializedState = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (serializedState === null) {
        return null;
      }
      const parsedState = JSON.parse(serializedState);
      if (!isValidState(parsedState)) {
        console.warn("[Store] Parsed state:", parsedState);
        throw new Error("Persisted state is invalid");
      }
      return parsedState;
    } catch (error) {
      console.error(`[Store] Could not load state from local storage: ${error.message}`);
      return null;
    }
  }
  savePersistedState() {
    try {
      const state = this.store.state;
      const serializedState = JSON.stringify(state);
      localStorage.setItem(LOCAL_STORAGE_KEY, serializedState);
    } catch (error) {
      console.error("[Store] Could not save state to local storage:", error);
    }
  }
  // ---------- Wallets ----------------------------------------------- //
  initializeWallets(walletsConfig) {
    console.info("[Manager] Initializing wallets...");
    for (const walletConfig of walletsConfig) {
      let walletId;
      let walletOptions;
      let walletMetadata;
      if (typeof walletConfig === "string") {
        walletId = walletConfig;
      } else {
        const { id, options, metadata } = walletConfig;
        walletId = id;
        walletOptions = options;
        walletMetadata = metadata;
      }
      const walletMap = createWalletMap();
      const WalletClass = walletMap[walletId];
      if (!WalletClass) {
        console.error(`[Manager] Wallet not found: ${walletId}`);
        continue;
      }
      const walletInstance = new WalletClass({
        id: walletId,
        metadata: walletMetadata,
        store: this.store,
        options: walletOptions,
        subscribe: this.subscribe
      });
      this._clients.set(walletId, walletInstance);
      console.info(`[Manager] \u2705 Initialized ${walletId}`);
    }
    const state = this.store.state;
    const connectedWallets = Object.keys(state.wallets);
    for (const walletId of connectedWallets) {
      if (!this._clients.has(walletId)) {
        console.warn(`[Manager] Connected wallet not found: ${walletId}`);
        removeWallet(this.store, { walletId });
      }
    }
    if (state.activeWallet && !this._clients.has(state.activeWallet)) {
      console.warn(`[Manager] Active wallet not found: ${state.activeWallet}`);
      setActiveWallet(this.store, { walletId: null });
    }
  }
  get wallets() {
    return [...this._clients.values()];
  }
  getWallet(walletId) {
    return this._clients.get(walletId);
  }
  async resumeSessions() {
    const promises = this.wallets.map((wallet) => wallet?.resumeSession());
    await Promise.all(promises);
  }
  // ---------- Network ----------------------------------------------- //
  initNetworkConfig(network, config) {
    console.info("[Manager] Initializing network...");
    let networkConfig = createDefaultNetworkConfig();
    if (isNetworkConfigMap(config)) {
      networkConfig = deepMerge(networkConfig, config);
    } else {
      networkConfig[network] = deepMerge(networkConfig[network], config);
    }
    console.info("[Manager] Algodv2 config:", networkConfig);
    return networkConfig;
  }
  createAlgodClient(config) {
    console.info(`[Manager] Creating Algodv2 client for ${this.activeNetwork}...`);
    const { token = "", baseServer, port = "", headers = {} } = config;
    return new algosdk8.Algodv2(token, baseServer, port, headers);
  }
  setActiveNetwork(networkId) {
    setActiveNetwork(this.store, { networkId });
    this.algodClient = this.createAlgodClient(this.networkConfig[networkId]);
  }
  get activeNetwork() {
    return this.store.state.activeNetwork;
  }
  // ---------- Active Wallet ----------------------------------------- //
  get activeWallet() {
    const state = this.store.state;
    const activeWallet = this.wallets.find((wallet) => wallet.id === state.activeWallet);
    if (!activeWallet) {
      return null;
    }
    return activeWallet;
  }
  get activeWalletAccounts() {
    if (!this.activeWallet) {
      return null;
    }
    return this.activeWallet.accounts;
  }
  get activeWalletAddresses() {
    if (!this.activeWallet) {
      return null;
    }
    return this.activeWallet.accounts.map((account) => account.address);
  }
  get activeAccount() {
    if (!this.activeWallet) {
      return null;
    }
    return this.activeWallet.activeAccount;
  }
  get activeAddress() {
    if (!this.activeAccount) {
      return null;
    }
    return this.activeAccount.address;
  }
  // ---------- Sign Transactions ------------------------------------- //
  get signTransactions() {
    if (!this.activeWallet) {
      throw new Error("[Manager] No active wallet found!");
    }
    return this.activeWallet.signTransactions;
  }
  /**
   * A function which can sign transactions from an atomic transaction group. The logic will be
   * specific to each wallet, but the function will always return a promise that resolves to an
   * array of encoded signed transactions matching the length of the indexesToSign array.
   *
   * @see https://github.com/algorand/js-algorand-sdk/blob/v2.6.0/src/signer.ts#L7-L18
   *
   * @param txnGroup - The atomic group containing transactions to be signed
   * @param indexesToSign - An array of indexes in the atomic transaction group that should be signed
   * @returns A promise which resolves an array of encoded signed transactions. The length of the
   *   array will be the same as the length of indexesToSign, and each index i in the array
   *   corresponds to the signed transaction from txnGroup[indexesToSign[i]]
   */
  get transactionSigner() {
    if (!this.activeWallet) {
      throw new Error("[Manager] No active wallet found!");
    }
    return this.activeWallet.transactionSigner;
  }
  /**
   * A wrapper around `TransactionSigner` that also has the sender address (the current active
   * account). Can be used to produce a `TransactionWithSigner` object ready to be passed to an
   * AtomicTransactionComposer's `addTransaction` method.
   *
   * @see https://github.com/algorandfoundation/algokit-utils-ts/blob/v4.0.0/docs/code/modules/index.md#gettransactionwithsigner
   */
  get transactionSignerAccount() {
    if (!this.activeAddress) {
      throw new Error("[Manager] No active account found!");
    }
    return {
      addr: this.activeAddress,
      signer: this.transactionSigner
    };
  }
};
export {
  BaseWallet,
  DeflyWallet,
  ExodusWallet,
  KmdWallet,
  MnemonicWallet,
  NetworkId,
  PeraWallet,
  WalletConnect,
  WalletId,
  WalletManager,
  defaultState
};
//# sourceMappingURL=index.js.map